import { z } from 'zod';
import { objectIdSchema } from './common.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';
import {
  DIRECTORY_OFFICERS_MAX_LIMIT,
  DIRECTORY_OFFICERS_PAGE_SIZE,
  DIRECTORY_SEARCH_MIN_LENGTH,
} from '../constants/directory.constant';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C1g §2, §3] THE OFFICER APP'S DIRECTORY — a nodal walking their own subtree.
 *
 *  ⚠️ IT IS VIEW ONLY, AND THAT IS STRUCTURAL RATHER THAN A CONVENTION (§2f). There is no
 *  create, update, transfer or role-change shape in this file, `directory.service.ts` imports
 *  no writer and opens no session except the one `recordAudit` requires, and every route is a
 *  `GET` bar the call marker. Editing an officer lives in the admin panel and always will.
 *
 *  ─── ⚠️ §2b's SCOPE IS `ancestors`, NOT `visible_org_unit_ids` ────────────────────
 *
 *  The brief says "the same `visible_org_unit_ids` path as §1f". That column is declared on
 *  `arrangements`, `standing_orders` and `allocation_fills` and on NOTHING ELSE — CLAUDE.md §7
 *  says in terms that it "IS NOT ON `org_units` AND MUST NOT BE ADDED… an org unit's own
 *  visibility IS `ancestors`", and under `strict: 'throw'` writing it would raise a
 *  StrictModeError. So the directory is scoped by `orgUnitScopeFilter` /
 *  `resolveSubtreeOrgUnitIds` — the mechanism every officer and org read in this system
 *  already uses — and it adds no FR-3.6 read site at all.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [§3a] EXACTLY FIVE FIELDS AND AN ID. NOT the service number, NOT the access role, and
 *  ⚠️ NOT `meta`.
 *
 *  Confirmed with the client. `officers.meta` is the INGEST's provenance — `ingestion_remarks`
 *  records synthesised phone numbers and unresolved sections, and `phone_synthesised` gates
 *  AUTHENTICATION. It is an operator's audit trail and was never meant to sit beside an
 *  officer's name on a phone.
 *
 *  ⚠️ IT IS A PROJECTION, NEVER A REUSE OF `officerSchema` — `officerOps.service.ts`'s rule,
 *  and the stronger case of it. That DTO carries twenty-three fields including `accessRole`,
 *  `serviceNumber`, `isActive` and both timestamps; forwarding it would put the dashboard's
 *  every FUTURE column onto the officer contract silently, and this one renders phone numbers.
 *  `meta` happens not to be on `officerSchema` today — which is exactly the kind of fact that
 *  changes without anybody connecting it to this screen.
 *
 *  ⚠️ THE ID IS ON THE WIRE AND IS NOT ONE OF THE FIVE. It is what `POST …/call` names, and
 *  `directory.test.ts` pins this object's ROOT KEY SET so a sixth DISPLAYED field cannot creep
 *  in behind it ([P6d] CLAIM 3's shape).
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const directoryOfficerSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  /** ⚠️ REQUIRED KEYS WITH NULLABLE VALUES — the profile renders a labelled row either way. */
  rank: z.object({ id: objectIdSchema, name: z.string(), shortName: z.string() }).nullable(),
  /** The POSTING. */
  orgUnit: orgUnitSummarySchema.nullable(),
  /**
   * ⚠️ [§2d] INLINE, WITH NO METER AND NO REVEAL STEP — CONFIRMED WITH THE CLIENT, AND A
   * DIVERGENCE FROM [C1b §5] THAT IS RECORDED RATHER THAN QUIET.
   *
   * That rule governs a DUTY ROSTER, where the subject may be an officer from another division
   * whom the caller has never administered — so the number is absent from the payload
   * entirely, metered per caller and audited per disclosure. This is a nodal reading their OWN
   * subtree's establishment, which is the population they administer.
   *
   * ⚠️ AND IT IS PARITY WITH THE DASHBOARD RATHER THAN A NEW DISCLOSURE CLASS: `officerSchema.
   * phone` is a REQUIRED field and `GET /officers` has always returned every in-scope
   * officer's number inline, un-metered and un-audited. This surface is STRICTER than the one
   * it mirrors, because it writes an audit row that one does not.
   */
  phone: z.string(),
  email: z.string().nullable(),
});

/** A child node in the walk. `officerCount` is this unit's own, never its subtree's. */
export const directoryChildSchema = z.object({
  unit: orgUnitSummarySchema,
  officerCount: z.number().int().nonnegative(),
  /**
   * ⚠️ [amended C1h §5b] HOW MANY UNITS SIT BELOW THIS ONE — a COUNT, where this was the boolean
   * `hasChildren`.
   *
   * §5b asks for each node to show its officer count AND its child-unit count. The boolean could
   * only ever answer "is there a drill", and the number answers both — `> 0` IS that boolean, so
   * the client derives it rather than being sent two facts that can disagree.
   *
   * ⚠️ IT COSTS NO EXTRA READ. `directory.service.ts` already ran a grouped query over the
   * children to decide the boolean; it groups with `$sum` instead of `distinct`, on the same
   * `{parent_id, name}` prefix, in the same round trip.
   *
   * ⚠️ AND `GET /org-units/counts` COULD NOT HAVE SERVED THIS — §5c's premise is false. It is
   * `dashboardStack('org.read')` with the DEFAULT `DASHBOARD_ONLY` audience, so a `pams:officer`
   * token is refused; and it returns four OFFICER figures and no child-unit count at all.
   */
  childUnitCount: z.number().int().nonnegative(),
});

export const directoryNodeSchema = z.object({
  unit: orgUnitSummarySchema,
  /**
   * ⚠️ ROOT-FIRST AND **EXCLUDING SELF**, exactly as `org_units.ancestors` is stored, and
   * TRIMMED TO THE CALLER'S OWN SCOPE. A breadcrumb naming a node above the caller's root
   * would disclose the shape of the tree above them, which is the one thing a subtree scope is
   * for — and it would offer a link every request behind it answers 404.
   */
  breadcrumb: z.array(orgUnitSummarySchema),
  children: z.array(directoryChildSchema),
  officerCount: z.number().int().nonnegative(),
  /**
   * ⚠️ [§3c] WHY THE NODALS TAB IS ABSENT RATHER THAN EMPTY. "An empty tab that exists for
   * every unit is noise; one that appears when there is something in it is information."
   * A count rather than the rows: the tab renders the same paginated list with a filter, so
   * shipping the nodals twice would be two answers to one question.
   */
  nodalCount: z.number().int().nonnegative(),
});

export const directoryNodeQuerySchema = z.object({
  /** Absent means the root of the caller's own scope — never "the whole tree". */
  parentId: objectIdSchema.optional(),
});

export const directoryOfficersParamsSchema = z.object({ id: objectIdSchema });

export const directoryOfficersQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(DIRECTORY_OFFICERS_MAX_LIMIT).default(DIRECTORY_OFFICERS_PAGE_SIZE),
  /**
   * ⚠️ [§3b] A MINIMUM LENGTH, because below it every officer in the unit matches — which is
   * the unbounded read the page size exists to prevent, reached through a filter.
   */
  search: z.string().trim().min(DIRECTORY_SEARCH_MIN_LENGTH).max(120).optional(),
  /**
   * ⚠️ [§3c] THE NODALS TAB IS THIS FILTER, NOT A SECOND ROUTE. One list, one audit path, one
   * page shape — and a second endpoint would be a second place for the scope check to be
   * written and therefore a second place for it to be forgotten.
   *
   * ⚠️ IT IS "HOLDS ANY ACCESS ROLE", NEVER ONE NAMED ROLE. `accessRole !== 'NONE'` is
   * `identity.constant.ts`'s own definition of "flagged nodal" (FR-3.2), and a client picking
   * a specific role would be reading the permission model on a screen that shows none of it.
   */
  nodalsOnly: z.stringbool().default(false),
});

export const directoryCallParamsSchema = z.object({ officerId: objectIdSchema });
